import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/context/auth-context';
import { apiFetch } from '@/lib/api';

interface Subscription {
  plan_id: string;
  status: string;
  paypal_subscription_id?: string;
  paddle_subscription_id?: string;
  billing_provider?: 'paypal' | 'paddle';
  current_period_end?: number;
}

interface PaymentContextType {
  subscription: Subscription | null;
  loading: boolean;
  refreshSubscription: () => Promise<void>;
  isPro: boolean;
  isElite: boolean;
  isEnterprise: boolean;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const PaymentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshSubscription = async () => {
    if (!user) {
      setSubscription({ plan_id: 'FREE', status: 'none' });
      setLoading(false);
      return;
    }
    
    try {
      const response = await apiFetch('/api/payments/status');
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshSubscription();

    // PayPal Redirect Verification Logic
    const handleRedirectVerification = async () => {
      const params = new URLSearchParams(window.location.search);
      const subscriptionId = params.get('subscription_id');
      const orderId = params.get('token'); // PayPal uses 'token' for Order ID in redirect

      if (subscriptionId) {
        console.log('[Payment] Detected subscription_id in URL, verifying...', subscriptionId);
        try {
          const res = await apiFetch('/api/payments/verify-subscription', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ subscriptionId }),
          });
          if (res.ok) {
            console.log('[Payment] Subscription verified via redirect');
            await refreshSubscription();
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname);
          }
        } catch (err) {
          console.error('[Payment] Redirect verification failed:', err);
        }
      } else if (orderId) {
        console.log('[Payment] Detected order token in URL, capturing...', orderId);
        try {
          const res = await apiFetch('/api/payments/capture-order', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ orderId }),
          });
          if (res.ok) {
            console.log('[Payment] Order captured via redirect');
            await refreshSubscription();
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname);
          }
        } catch (err) {
          console.error('[Payment] Redirect order capture failed:', err);
        }
      }
    };

    if (user) {
      handleRedirectVerification();
    }
  }, [user]);

  const planId = (subscription?.plan_id || '').toLowerCase();
  const isPro = planId === 'creator_pro' || planId === 'studio_elite' || planId === 'enterprise';
  const isElite = planId === 'studio_elite' || planId === 'enterprise';
  const isEnterprise = planId === 'enterprise';

  return (
    <PaymentContext.Provider value={{ subscription, loading, refreshSubscription, isPro, isElite, isEnterprise }}>
      {children}
    </PaymentContext.Provider>
  );
};

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};
